import { assert } from './assert-condition';
import { getFormattedShortWeekDays } from './date-utils';

export function getFormattedLocalTimeScheduleForGrid({ weekdays, time }: { weekdays?: string; time?: number }): string {
    return getFormattedScheduleForGrid({
        weekdays,
        time,
        isUTC: false
    });
}

export function getFormattedUTCScheduleForGrid({ weekdays, time }: { weekdays?: string; time?: number }): string {
    return getFormattedScheduleForGrid({
        weekdays,
        time,
        isUTC: true
    });
}

function getFormattedScheduleForGrid({ weekdays, time, isUTC = false }: { weekdays?: string; time?: number; isUTC?: boolean }): string {
    if (weekdays == null || time == null) return '';

    const info = isUTC ? getUtcScheduleInfo(time, weekdays) : getLocalScheduleInfo(time, weekdays);

    return formatScheduleOutput(info);
}

function getUtcScheduleInfo(
    time: number,
    weekdays: string
): {
    weekdays: string;
    hours: number;
    minutes: number;
    timeZone: string;
} {
    assert(time >= 0 && time <= 1439, 'Time must be between 0 and 1439');
    const hours = Math.floor(time / 60);
    const minutes = time % 60;

    return {
        weekdays,
        hours,
        minutes,
        timeZone: 'UTC'
    };
}

export function getLocalScheduleInfoFromUTC(
    utcTime: number,
    utcWeekdays: number[]
): {
    weekdays: string;
    hours: number;
    minutes: number;
    timeZone: string;
} {
    return getLocalScheduleInfo(utcTime, utcWeekdays.join(','));
}

function getLocalScheduleInfo(
    utcTime: number,
    utcWeekdays: string
): {
    weekdays: string;
    hours: number;
    minutes: number;
    timeZone: string;
} {
    assert(utcTime >= 0 && utcTime <= 1439, 'Time must be between 0 and 1439');

    const { localDate, weekdays } = getLocalDate(utcTime, utcWeekdays);

    return {
        weekdays,
        hours: localDate.getHours(),
        minutes: localDate.getMinutes(),
        timeZone: getLocalTimeZoneName()
    };
}

function formatScheduleOutput({
    weekdays,
    hours,
    minutes,
    timeZone
}: {
    weekdays: string;
    hours: number;
    minutes: number;
    timeZone: string;
}): string {
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const formattedDays = getFormattedShortWeekDays(weekdays);
    return `On ${formattedDays} at ${timeStr} (${timeZone})`;
}

function getLocalDate(utcTime: number, utcWeekdays: string): { localDate: Date; weekdays: string } {
    const now = new Date();
    const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0));
    utcDate.setUTCMinutes(utcTime);
    const localDate = new Date(utcDate.getTime());
    const weekdays = convertWeekDays(utcDate.getUTCDay(), localDate.getDay(), utcWeekdays);

    return { localDate, weekdays };
}

export function getUtcScheduleInfoFromLocalTime(time: number, weekdays: number[]): { time: number; weekdays: number[] } {
    const { utcDate, utcWeekdays } = getUTCDateFromLocal(time, weekdays.join(','));
    const utcTime = utcDate.getHours() * 60 + utcDate.getMinutes();
    return { time: utcTime, weekdays: utcWeekdays.split(',').map(Number) };
}

function getUTCDateFromLocal(localTime: number, localWeekdays: string): { utcDate: Date; utcWeekdays: string } {
    const localDate = new Date();
    localDate.setHours(0);
    localDate.setMinutes(localTime);

    const utcDate = new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);
    const utcWeekdays = convertWeekDays(localDate.getDay(), utcDate.getDay(), localWeekdays);

    return { utcDate, utcWeekdays };
}

function getLocalTimeZoneName(): string {
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZone =
        new Intl.DateTimeFormat('en-US', {
            timeZone: currentTimeZone,
            timeZoneName: 'short'
        })
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value || '';
    return timeZone;
}

function convertWeekDays(fromDay: number, toDay: number, weekdays: string): string {
    const dayOffset = toDay - fromDay;

    const toWeekDays = weekdays?.split(',').map((key) => {
        const adjustedDay = +key + dayOffset;
        return adjustedDay > 7 ? adjustedDay % 7 : adjustedDay < 1 ? adjustedDay + 7 : adjustedDay;
    });

    return toWeekDays.join(',');
}
